import React, { useContext } from 'react';
import AuthenticatedLayout from './AuthenticatedLayout';
import UnauthenticatedLayout from './UnauthenticatedLayout';
import { PropsWithChildren, ReactNode, useEffect } from 'react';
import { navigate } from 'gatsby';
import { GlobalContext } from '../../context/GlobalContext';
import { clearFlow, isNewUserFlow } from '../../utils/userFlowStorage';
import CustomLayout from './CustomLayout';
import { PurchaseContext } from '../../context/PurchaseContext';
import { isCapacitorBuild } from '../../utils/platform';
import SplashAnimation from './SplashAnimation';
import {
  toSignIn,
  customLayoutPaths,
  staffOnboardingFlowPaths,
  applicationFlowPaths,
} from '../../routes';
import UserProfileFetchAlert from '../UserProfileFetchAlert';
import PromoPrompt from '../promo/PromoPrompt';
import ForceUpgradeGate from './ForceUpgradeGate';

export type NavCategoriesType = {
  title: string;
  mainNav: {
    header: string;
    description: string;
    links: { route: string; label: string }[];
    cta: {
      route: string;
      label: string;
    };
  };
  featureNav?: {
    header: string;
    links: {
      route: string;
      icon: ReactNode;
      header: string;
      description: string;
    }[];
    cta?: {
      route: string;
      label: string;
    };
  };
  blogNav?: boolean;
}[];

const Layout = (
  props: PropsWithChildren<{
    pathname: string;
    search?: string;
  }>
) => {
  const {
    updateUrlParams,
    userAuth: { isAuthenticated },
    jobToApply,
    urlParams,
    resetJobToApply,
    resetUrlParams,
  } = useContext(GlobalContext);
  const { getCatalog } = useContext(PurchaseContext);
  const [hasMounted, setHasMounted] = React.useState(false);
  const [rotationComplete, setRotationComplete] = React.useState(false);
  const searchDiscountCode = React.useMemo(() => {
    if (!props.search) {
      return undefined;
    }

    const params = new URLSearchParams(props.search);
    return (
      params.get('discount-code') || params.get('discountCode') || undefined
    );
  }, [props.search]);

  const isCustomLayoutPath = React.useMemo(
    () => customLayoutPaths.some((path) => props.pathname.startsWith(path)),
    [props.pathname]
  );

  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true);
    } else if (!isAuthenticated && !isCapacitorBuild) {
      getCatalog({
        discountCode: urlParams.discountCode ?? searchDiscountCode,
      });
    }
  }, [hasMounted, isAuthenticated]);

  useEffect(() => {
    if (!!props.search) {
      const search = new URLSearchParams(props.search);
      updateUrlParams(search);
    }

    //reset jobToApply and urlParams if user navigates away from application flow without applying to a job
    if (
      (!!jobToApply || urlParams.jobId) &&
      !applicationFlowPaths.some((path) => props.pathname.startsWith(path))
    ) {
      resetJobToApply();
      resetUrlParams({ jobId: undefined });
      clearFlow();
    } else if (
      //reset onboarding flow if user navigates away from onboarding flow paths
      isNewUserFlow() &&
      !staffOnboardingFlowPaths.some((path) => props.pathname.startsWith(path))
    ) {
      clearFlow();
    }
  }, [props.pathname, props.search, jobToApply, urlParams.jobId]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mainContent = document.getElementById('main-content');
      mainContent?.scrollTo(0, 0);
    }
  }, [props.pathname]);

  // The mobile app has no unauthenticated browsing surface — every public
  // route should bounce to the sign-in flow. Wait for mount so the hydration
  // render (always unauthenticated) doesn't trigger a redirect.
  useEffect(() => {
    if (!isCapacitorBuild || !hasMounted) return;
    if (isCustomLayoutPath) return;
    if (isAuthenticated) return;
    navigate(toSignIn, { replace: true });
  }, [hasMounted, isAuthenticated, isCustomLayoutPath]);

  return (
    <div id="layout-wrapper" className="d-flex flex-column dp-h-100dvh">
      {/* Mounted outside the custom-layout branch so the gate also covers
          the sign-in flow. Renders null unless the version gate is up. */}
      {isCapacitorBuild && <ForceUpgradeGate />}
      {isCapacitorBuild && (!hasMounted || !rotationComplete) && (
        <SplashAnimation onRotationEnd={() => setRotationComplete(true)} />
      )}
      {/* The promo pop-up is mounted once here at the root — a single
          persistent mount so it isn't unmounted (and re-suppressed) across the
          auth transition. The banner is mounted inside each layout instead (see
          Authenticated/Unauthenticated/StaffLayout) so it renders in that
          layout's own flow. Skipped on custom (login) paths and on capacitor
          until the user is authenticated. */}
      {!isCustomLayoutPath &&
        ((isAuthenticated && hasMounted) || !isCapacitorBuild) && (
          <PromoPrompt mode="popup" />
        )}
      {isCustomLayoutPath ? (
        <CustomLayout isLogin={props.pathname.startsWith(toSignIn)}>
          {props.children}
        </CustomLayout>
      ) : (
        <>
          {isAuthenticated && hasMounted ? (
            <AuthenticatedLayout pathname={props.pathname}>
              <UserProfileFetchAlert />
              {props.children}
            </AuthenticatedLayout>
          ) : isCapacitorBuild ? null : (
            <UnauthenticatedLayout>{props.children}</UnauthenticatedLayout>
          )}
        </>
      )}
    </div>
  );
};

export default Layout;
